import React, { useRef, useState, useEffect } from "react";
import AutoComplete from "react-google-autocomplete";
import CustomInput from "../../common/CustomInput";
import CustomSelect from "../../common/CustomSelect";
import { googlePlaceToAddress } from "../../../lib/googlePlaceToAddress";

const BillingAddress = (props) => {
  const {
    address,
    countries,
    // hasCashPrizeDisclaimer,
    isManualPay,
    onChange,
    onChangeEvent,
    submitAttempted,
    requireDonorAddress,
  } = props;

  //Need to get the latest onChange function so it holds the most recent order details
  const latestOnChange = useRef();
  const [autoAddress1, setAutoAddress1] = useState("");
  const [addressNotFound, setAddressNotFound] = useState(false);

  // we maintain this ref (which tracks if the address1 input is focused) in order to immediately invoke the onChange prop when the address1 input change handler is called without a focus - via browser autofill changes
  const address1Focused = useRef(false);

  const address2Ref = useRef(null);
  const setAddress2Focus = () => {
    address2Ref.current && address2Ref.current.focus();
  };

  const handleSelectAddress = (googlePlace) => {
    const addressInfo = googlePlaceToAddress(googlePlace);

    // sometimes google shows an address that doesn't really exist so some pieces are missing
    // handle if missing info which will allow user to enter manually
    if (!addressInfo || !addressInfo.streetNumber || !addressInfo.streetName) {
      setAddressNotFound(true);
      return;
    }

    const { streetNumber, streetName, city, locality, country, state, zip } =
      addressInfo;

    const fullAddress1 = `${streetNumber} ${streetName}`;

    setAutoAddress1(fullAddress1);
    latestOnChange.current("address.address1", fullAddress1);
    latestOnChange.current("address.address2", "");
    latestOnChange.current("address.city", locality || city || "");
    latestOnChange.current("address.country", country || "");
    latestOnChange.current("address.state", state || "");
    latestOnChange.current("address.zip", zip || "");

    setAddress2Focus();
  };

  const preventFormSubmitOnEnter = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const onChangeCountry = (name, value) => {
    const resetState = address.state && value !== address.country;
    onChange(name, value, resetState ? { "address.state": "" } : null);
  };

  const showFieldError = (fieldName) => {
    return (
      submitAttempted &&
      isManualPay &&
      requireDonorAddress &&
      !address[fieldName]
    );
  };
  const optionalTag = () => <span className="optional-tag">(optional)</span>;

  useEffect(() => {
    latestOnChange.current = onChange;
  }, [onChange]);

  return (
    <div>
      <p className="fs-20 fw-600 mt-24 mb-32">Billing Info</p>
      <div className="checkout-grid">
        <div
          className="flex flex-align-center mb-12 mobile-block google-search"
          style={{ gridColumn: "span 2" }}
        >
          <label>Address {!requireDonorAddress && optionalTag()}</label>
          <div className="full-width">
            <AutoComplete
              apiKey="AIzaSyBUUhBeNNMuZiNJDYh91-NnTT3BRq1Ohaw"
              className={`custom-input ${
                showFieldError("address1") ? "error" : ""
              }`}
              inputAutocompleteValue="address-line1"
              onKeyDown={preventFormSubmitOnEnter}
              onPlaceSelected={handleSelectAddress}
              placeholder="Enter your street address"
              value={autoAddress1}
              onChange={(e) => {
                setAutoAddress1(e.target.value);
                // if address1 is not focused, this change handler was triggered from an external source (browser autofill), and we perform the 'onBlur logic' immediately, because there will not be an onBlur event invocation
                if (!address1Focused.current) {
                  if (addressNotFound) setAddressNotFound(false);
                  onChange("address.address1", e.target.value);
                }
              }}
              onFocus={() => (address1Focused.current = true)}
              options={{
                types: [],
                fields: ["address_components"],
              }}
              onBlur={(_) => {
                address1Focused.current = false;
                // for manual entry
                if (addressNotFound) setAddressNotFound(false);
                onChange("address.address1", autoAddress1);
              }}
            />
            {addressNotFound && (
              <p className="error-message mb-12">
                Address not found. Please enter it manually or try a different
                address.
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-align-center mb-12 mobile-block">
          <label>
            Street <br /> Address 2 {!requireDonorAddress && optionalTag()}
          </label>
          <input
            autoComplete="address-line2"
            className="custom-input full-width"
            name="address.address2"
            onChange={onChangeEvent}
            ref={address2Ref}
            type="text"
            value={address.address2}
          />
        </div>
        <div className={`flex flex-align-center mb-12 mobile-block `}>
          <label>Country {!requireDonorAddress && optionalTag()}</label>
          <CustomSelect
            autoComplete="country"
            className={`${showFieldError("country") ? "error" : ""}`}
            clearable={true}
            name="address.country"
            onChange={onChangeCountry}
            options={
              countries &&
              countries
                .sort((country) =>
                  country.code === "US" || country.code === "CA" ? -1 : 1
                )
                .map((country) => ({
                  display: country.name,
                  value: country.name,
                }))
            }
            placeholder="Select Country"
            value={address.country}
          />
        </div>
        <div className="flex flex-align-center mb-12 mobile-block">
          <label>City {!requireDonorAddress && optionalTag()}</label>
          <CustomInput
            autoComplete="address-level2"
            className={`${showFieldError("city") ? "error" : ""}`}
            name="address.city"
            onChange={onChangeEvent}
            type="text"
            value={address.city}
          />
        </div>
        <div className="flex flex-align-center mb-12 mobile-block">
          <label>State {!requireDonorAddress && optionalTag()}</label>
          {address.country === "USA" ||
          address.country === "Canada" ||
          address.country === "Mexico" ? (
            <CustomSelect
              autoComplete="address-level1"
              className={`custom-input ${
                showFieldError("state") ? "error" : ""
              }`}
              clearable={true}
              disabled={!address.country}
              name="address.state"
              onChange={onChange}
              options={
                address.country &&
                (
                  (
                    countries.find(
                      (country) => country.name === address.country
                    ) || {}
                  ).states || []
                ).map((state) => ({
                  display: state.name,
                  value: state.code,
                }))
              }
              value={address.state}
            />
          ) : (
            <CustomInput
              className={`${showFieldError("state") ? "error" : ""}`}
              autoComplete="address-level1"
              name="address.state"
              onChange={onChangeEvent}
              type="text"
              value={address.state}
            />
          )}
        </div>
        <div className="flex flex-align-center mb-12 mobile-block">
          <label>Zip {!requireDonorAddress && optionalTag()}</label>
          <div className="full-width">
            <CustomInput
              autoComplete="postal-code"
              className={`billing-postal-code ${
                showFieldError("zip") ? "error" : ""
              }`}
              name="address.zip"
              onChange={onChangeEvent}
              type="text"
              value={address.zip}
            />
          </div>
        </div>
      </div>
      {/* {hasCashPrizeDisclaimer && (
        <p className="cash-prize-disclaimer">
          *Texas residents are not eligible for cash prizes
        </p>
      )} */}
    </div>
  );
};
export default React.memo(BillingAddress);
